import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { ApiError } from '../lib/api'

interface Props {
  email: string
  /** Resolves once the account is gone; rejects with an ApiError otherwise. */
  onDelete: (password: string) => Promise<void>
  onExport: () => void
  onClose: () => void
}

/**
 * The one way to delete an account from inside the app. It asks for the
 * password again rather than a typed "DELETE": a signed-in session is all
 * someone at a borrowed or unlocked device has, and this can't be undone.
 */
export default function DeleteAccountModal({ email, onDelete, onExport, onClose }: Props) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onDelete(password)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.fields?.password ?? err.message
          : 'Something went wrong. Your account has not been deleted.',
      )
      setBusy(false)
    }
  }

  return (
    <Modal title="Delete your account" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p className="text-sm text-ink/80 mb-3">
          This deletes <strong className="text-ink break-all">{email}</strong> and everything in it: every
          bank account, its plan and the sandbox. It can't be undone.
        </p>
        <p className="text-sm text-ink/80 mb-3">
          Copies in our backups are removed within 30 days. We'll send one last email to confirm it's done.
        </p>
        <p className="text-sm text-ink/80 mb-4">
          Want to keep a copy?{' '}
          <button type="button" onClick={onExport} className="text-accent hover:underline">
            Export your data
          </button>{' '}
          first.
        </p>

        <Field label="Your password">
          <input
            className={inputClass}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            aria-invalid={error ? true : undefined}
          />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-rag-bad -mt-2 mb-3">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-line">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || password === ''}
            className="px-3 py-1.5 text-sm rounded-sm bg-rag-bad text-white hover:bg-rag-bad/90 disabled:opacity-50"
          >
            {busy ? 'Deleting…' : 'Delete my account'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
